/**
 * AIChatButton - Icon button for toggling the AI chat panel
 * Shows a subtle glow animation when chat is actively streaming
 */
import React from 'react';
import { Sparkles } from 'lucide-react';

interface AIChatButtonProps {
  onClick: () => void;
  isStreaming?: boolean;
  size?: number;
  buttonSize?: string;
}

const AIChatButton: React.FC<AIChatButtonProps> = ({ onClick, isStreaming = false, size = 20, buttonSize = '2.5rem' }) => {
  return (
    <>
      <button
        onClick={onClick}
        style={{
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          justifyContent: 'center',
          width: buttonSize,
          height: buttonSize,
          gap: 0,
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: '0.5rem',
          color: isStreaming ? 'var(--tc-text-selected, #6BA6F3)' : 'var(--tc-text-placeholder, #EEEEEE)',
          cursor: 'pointer',
          position: 'relative',
          transition: 'background-color 0.2s, color 0.2s',
          flexShrink: 0,
          animation: isStreaming ? 'aiChatPulse 2s ease-in-out infinite' : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--tc-overlay-wash, rgba(255, 255, 255, 0.04))';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        aria-label="AI Chat"
        title="AI Assistant"
      >
        <Sparkles size={size} />
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', marginTop: 1 }}>AI</span>
        {isStreaming && (
          <div
            style={{
              position: 'absolute',
              top: '0.25rem',
              right: '0.25rem',
              width: '0.5rem',
              height: '0.5rem',
              backgroundColor: 'var(--tc-status-info, #2196F3)',
              borderRadius: '50%',
              animation: 'aiChatDot 1.5s ease-in-out infinite',
            }}
          />
        )}
      </button>
      <style>{`
        @keyframes aiChatPulse {
          0%, 100% { filter: drop-shadow(0 0 0px transparent); }
          50% { filter: drop-shadow(0 0 6px rgba(59, 130, 246, 0.4)); }
        }
        @keyframes aiChatDot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </>
  );
};

export default AIChatButton;
